import React, { useState, useRef } from 'react';
import { Form, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCloudUpload, 
  faSpinner, 
  faBrain, 
  faCheckCircle, 
  faExclamationTriangle,
  faFileUpload,
  faRobot
} from '@fortawesome/free-solid-svg-icons';

interface DocumentTypeUploadProps {
  type: string;
  label: string;
  description: string;
  accept: string;
  icon: any;
  processingStatus?: 'uploading' | 'processing' | 'searchable' | 'failed' | null;
  onUpload: (files: File[]) => void;
  maxFiles?: number;
  disabled?: boolean;
}

const DocumentTypeUpload: React.FC<DocumentTypeUploadProps> = ({
  type,
  label,
  description,
  accept,
  icon,
  processingStatus,
  onUpload,
  maxFiles = 5,
  disabled = false
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (disabled) return;
    
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (disabled) return;
    
    const files = Array.from(e.target.files || []);
    handleFiles(files);
  };

  const handleFiles = (files: File[]) => {
    // Validate file types
    const allowedExtensions = accept.split(',').map(ext => ext.trim().toLowerCase());
    const validFiles = files.filter(file => {
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      return allowedExtensions.includes(fileExtension) || allowedExtensions.includes(file.type);
    });

    if (validFiles.length !== files.length) {
      alert(`Some files were rejected. Only ${accept} files are allowed.`);
    }

    // Limit number of files
    const filesToProcess = validFiles.slice(0, maxFiles);
    
    if (filesToProcess.length > 0) {
      setUploadedFiles(prev => [...prev, ...filesToProcess]);
      onUpload(filesToProcess);
    }
  };

  const onButtonClick = () => {
    if (disabled) return;
    fileInputRef.current?.click();
  };

  const getStatusBadge = () => {
    switch (processingStatus) {
      case 'uploading':
        return (
          <Badge bg="info" className="status-badge">
            <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
            Uploading...
          </Badge>
        );
      case 'processing':
        return (
          <Badge bg="warning" className="status-badge">
            <FontAwesomeIcon icon={faBrain} className="me-1" />
            AI Processing...
          </Badge>
        );
      case 'searchable':
        return (
          <Badge bg="success" className="status-badge">
            <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
            AI Searchable
          </Badge>
        );
      case 'failed':
        return (
          <Badge bg="danger" className="status-badge">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
            Processing Failed
          </Badge>
        );
      default:
        return null;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className={`document-upload-container ${disabled ? 'disabled' : ''}`}>
      {/* Header */}
      <div className="upload-header mb-3">
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <FontAwesomeIcon 
              icon={icon} 
              className="me-2" 
              style={{ color: '#8b5cf6' }}
            />
            <div>
              <h6 className="mb-0 fw-semibold" style={{ color: '#374151' }}>
                {label}
              </h6>
              <small className="text-muted">{description}</small>
            </div>
          </div>
          {getStatusBadge()}
        </div>
      </div>

      {/* Drop Zone */}
      <div 
        className={`dropzone ${dragActive ? 'drag-active' : ''} ${disabled ? 'disabled' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={onButtonClick}
        style={{
          border: `2px dashed ${dragActive ? '#8b5cf6' : 'rgba(139, 92, 246, 0.3)'}`,
          borderRadius: '12px',
          padding: '2rem',
          textAlign: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          background: dragActive 
            ? 'rgba(139, 92, 246, 0.1)' 
            : 'rgba(139, 92, 246, 0.05)',
          transition: 'all 0.3s ease',
          position: 'relative'
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple
          onChange={handleChange}
          style={{ display: 'none' }}
          disabled={disabled}
        />
        
        <div className="dropzone-content">
          <FontAwesomeIcon 
            icon={faCloudUpload} 
            size="2x" 
            className="mb-3" 
            style={{ color: disabled ? '#9ca3af' : '#8b5cf6' }}
          />
          
          <div className="mb-2">
            <p className="mb-1 fw-semibold" style={{ color: disabled ? '#9ca3af' : '#374151' }}>
              {dragActive ? 'Drop files here' : 'Drop files here or click to browse'}
            </p>
            <small className="text-muted">
              Accepts {accept} files • Max {maxFiles} files • Up to 50MB each
            </small>
          </div>
          
          {processingStatus === 'searchable' && (
            <div className="ai-enhancement-indicator mt-2">
              <FontAwesomeIcon icon={faRobot} className="me-1 text-success" />
              <small className="text-success fw-semibold">
                AI Enhanced & Searchable
              </small>
            </div>
          )}
        </div>
      </div>

      {/* Uploaded Files List */}
      {uploadedFiles.length > 0 && (
        <div className="uploaded-files-list mt-3">
          <small className="text-muted fw-semibold mb-2 d-block">
            Selected Files ({uploadedFiles.length})
          </small>
          <div className="files-container">
            {uploadedFiles.map((file, index) => (
              <div 
                key={index} 
                className="file-item d-flex align-items-center justify-content-between p-2 mb-1"
                style={{
                  background: 'rgba(139, 92, 246, 0.05)',
                  border: '1px solid rgba(139, 92, 246, 0.2)',
                  borderRadius: '8px'
                }}
              >
                <div className="d-flex align-items-center">
                  <FontAwesomeIcon 
                    icon={faFileUpload} 
                    className="me-2 text-primary" 
                    size="sm"
                  />
                  <div>
                    <div className="file-name" style={{ fontSize: '13px', fontWeight: '500' }}>
                      {file.name}
                    </div>
                    <div className="file-size" style={{ fontSize: '11px', color: '#6b7280' }}>
                      {formatFileSize(file.size)}
                    </div>
                  </div>
                </div>
                
                {processingStatus && (
                  <div className="file-status">
                    {processingStatus === 'uploading' && (
                      <FontAwesomeIcon icon={faSpinner} spin className="text-info" size="sm" />
                    )}
                    {processingStatus === 'processing' && (
                      <FontAwesomeIcon icon={faBrain} className="text-warning" size="sm" />
                    )}
                    {processingStatus === 'searchable' && (
                      <FontAwesomeIcon icon={faCheckCircle} className="text-success" size="sm" />
                    )}
                    {processingStatus === 'failed' && (
                      <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger" size="sm" />
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI Processing Info */}
      {(processingStatus === 'processing' || processingStatus === 'searchable') && (
        <div 
          className="ai-processing-info mt-3 p-3"
          style={{
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%)',
            border: '1px solid rgba(139, 92, 246, 0.2)',
            borderRadius: '10px'
          }}
        >
          <div className="d-flex align-items-start">
            <FontAwesomeIcon 
              icon={processingStatus === 'processing' ? faBrain : faRobot} 
              className={`me-2 mt-1 ${processingStatus === 'processing' ? 'text-warning' : 'text-success'}`}
            />
            <div>
              <small className="fw-semibold d-block" style={{ color: '#374151' }}>
                {processingStatus === 'processing' 
                  ? 'AI is analyzing your document...' 
                  : 'Your document is now AI-searchable!'
                }
              </small>
              <small className="text-muted">
                {processingStatus === 'processing' 
                  ? 'Creating semantic embeddings and analyzing content for automation use.'
                  : 'The AI can now find and use content from this document in automated workflows.'
                }
              </small>
            </div>
          </div>
        </div>
      )}

      {/* Styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .document-upload-container.disabled {
            opacity: 0.6;
          }
          
          .dropzone:hover:not(.disabled) {
            border-color: #8b5cf6 !important;
            background: rgba(139, 92, 246, 0.1) !important;
          }
          
          .dropzone.drag-active {
            transform: scale(1.02);
            box-shadow: 0 0 20px rgba(139, 92, 246, 0.3);
          }
          
          .status-badge {
            font-size: 10px;
            padding: 4px 8px;
            border-radius: 12px;
          }
          
          .file-item {
            transition: all 0.2s ease;
          }
          
          .file-item:hover {
            background: rgba(139, 92, 246, 0.1) !important;
          }
          
          .ai-enhancement-indicator {
            padding: 4px 8px;
            background: rgba(16, 185, 129, 0.1);
            border-radius: 8px;
            display: inline-block;
          }
        `
      }} />
    </div>
  );
};

export default DocumentTypeUpload;